import React, { useEffect, useState } from "react";
import { freight } from "../../lib/freightApi.js";
import LiveListen, { primeAudio } from "../LiveListen.jsx";
import { T as INK, mono } from "../../staff/ui.jsx";
import { Card, Button, Select, TextArea, Note, Quiet, C, errText } from "./ui.jsx";
import { CALL_REASONS, platformReason, MESSAGE_MAX, outcomeWords, nextAt } from "./dispatchView.js";

/**
 * "Call Marcus" (the schematic's one-load board): why, what Alfred tells them, and when.
 *
 * ⛔ THE SERVER DECIDES WHETHER THE PHONE RINGS. Consent, the carrier's stage, do-not-call, quiet
 * hours and calling hours are all checked there (backend index.mjs, core freightCalls.js), and a
 * refusal comes back in its own words. This form only says what it was told.
 *
 * What Alfred tells them travels as the load's note for this one call (backend lib.mjs
 * platformLoad), which the platform reads to Alfred as "Notes for the driver". ⛔ The hint says
 * exactly that, and no more: whether Alfred says it the way it was typed is for an ear test.
 */

const SETTLED = ["completed", "no_answer", "failed"];
// A call the platform has not settled in this long is left to "What the calls said" (core may never report it).
const GIVE_UP_MS = 15 * 60_000;

const REFUSAL_WORDS = {
  no_consent: "has not agreed to Alfred's calls, so Alfred will not call them.",
  do_not_call: "is on the do-not-call list.",
  driver_quiet_hours: "has quiet hours set, and it is inside them now.",
  quiet_hours: "is outside calling hours right now.",
  driver_local_hours: "is outside calling hours right now.",
  rehearsal_test_only: "is not a test phone, and this account is in rehearsal.",
  carrier_in_setup: "cannot be called while the account is being set up.",
  carrier_paused: "cannot be called while the account is paused.",
  not_configured: "cannot be called: calling is not connected on this server.",
  call_in_progress: "is on a call with Alfred right now. Try again when it ends.",
  driver_removed: "is no longer on your roster, so Alfred will not call them.",
  not_this_load: "is not the driver on this load. Reload the page to see who is.",
  // ⛔ May have gone (backend calls.mjs): never "did not go out".
  platform_timeout: "may have been called: the platform did not answer in time. Check What the calls said before calling again.",
};
export const refusalLine = (first, err) => {
  const code = err?.body?.error || null;
  if (code && REFUSAL_WORDS[code]) return `${first} ${REFUSAL_WORDS[code]}`;
  // ⛔ No word from our server at all (the connection dropped, or a proxy answered): it may have
  // placed the call before the answer was lost (review). Never "did not go out" on a guess.
  if (!code) return `We could not tell whether the call to ${first} went out. Check the calls before calling again.`;
  return `The call did not go out: ${errText(err)}`;
};

export function CallForm({ driver, reason, setReason, message, setMessage, busy, onCall, when, setWhen, time, setTime, ringing = false }) {
  const first = String(driver?.name || "").split(/\s+/)[0] || "them";
  const reasons = CALL_REASONS.map((r) => (r.v === "CHANGE" ? { ...r, label: `Tell ${first} about a change` } : r));
  const needsWords = reason === "CHANGE" && !message.trim();
  const idp = `call-${driver?.id ?? "x"}`;
  if (!driver) return <Quiet>No driver on this load yet, so there is nobody to call.</Quiet>;
  // A driver taken off the roster is not called (backend calls.mjs): no form to fill in.
  if (driver.active === false) return <Quiet>{`${driver.name} is no longer on your roster, so Alfred does not call them.`}</Quiet>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {!driver.consent && <Quiet>{`${first} has not agreed to Alfred's calls, so Alfred cannot call them yet.`}</Quiet>}
      <Select id={`${idp}-reason`} label="Reason for the call" value={reason} onChange={setReason} options={reasons} />
      <TextArea id={`${idp}-say`} label={`What Alfred tells ${first}`} value={message} onChange={(v) => setMessage(v.slice(0, MESSAGE_MAX))} rows={3}
        maxLength={MESSAGE_MAX}
        placeholder="For example: the receiver moved the appointment to 4:30, and the gate code is 2245."
        hint={reason === "CHANGE" ? "Given to Alfred as a note for this call. Say what changed." : "Given to Alfred as a note for this call. Leave it empty for a normal check-in."} />
      <fieldset style={{ border: 0, margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        <legend style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>When</legend>
        <div style={{ display: "flex", gap: "8px 24px", flexWrap: "wrap", alignItems: "center", fontSize: 16 }}>
          <label htmlFor={`${idp}-now`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input id={`${idp}-now`} type="radio" name={`${idp}-when`} checked={when === "now"} onChange={() => setWhen("now")} /> Now
          </label>
          <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label htmlFor={`${idp}-later`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input id={`${idp}-later`} type="radio" name={`${idp}-when`} checked={when === "later"} onChange={() => setWhen("later")} /> Later, at
            </label>
            <input type="time" aria-label="Time for the call" value={time} onChange={(e) => { setTime(e.target.value); setWhen("later"); }}
              style={{ minHeight: 44, font: "inherit", fontSize: 15, border: `1px solid ${C.field}`, borderRadius: 8, padding: "0 8px" }} />
          </span>
        </div>
        {when === "later" && nextAt(time) && <span style={{ fontSize: 14, color: C.muted }}>{`Alfred calls ${first} at ${nextAt(time).at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} ${nextAt(time).day}.`}</span>}
      </fieldset>
      <div>
        <Button disabled={busy || (ringing && when === "now") || !driver.consent || needsWords || (when === "later" && !nextAt(time))} onClick={onCall} style={{ minHeight: 48, fontSize: 16, padding: "0 22px" }}>
          {busy ? (when === "later" ? "Saving…" : "Calling…") : when === "later" ? `Call ${first} later` : `Call ${first}`}
        </Button>
      </div>
    </div>
  );
}

/** A call in progress or just finished, read from the platform every few seconds. */
export function CallProgress({ call, name, lost, doubt }) {
  if (!call) return null;
  const first = String(name || "").split(/\s+/)[0] || "them";
  const settled = SETTLED.includes(call.status);
  return (
    <div role="status" aria-live="polite" style={{ fontSize: 16, color: C.ink }}>
      {settled ? `The call to ${first}: ${outcomeWords(call)}.`
        : lost ? `No word from the platform on the call to ${first}. It shows under What the calls said once it reports.`
        : doubt ? `No word yet from the platform on the call to ${first}. It may have gone through.`
        : `Alfred is calling ${first}…`}
    </div>
  );
}

export default function CallDriver({ driver, load, onDone }) {
  const [reason, setReason] = useState("CHECK_IN");
  const [message, setMessage] = useState("");
  const [when, setWhen] = useState("now");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [call, setCall] = useState(null);
  const [lost, setLost] = useState(false);
  const [doubt, setDoubt] = useState(false);
  const first = String(driver?.name || "").split(/\s+/)[0] || "them";
  const ringing = !!call && !SETTLED.includes(call.status) && !lost;

  // Ask the platform how the call went until it settles (nothing pushes into this app). ⛔ It stops
  // (review) when the call is gone (404) or nothing has settled it in GIVE_UP_MS.
  useEffect(() => {
    if (!call || SETTLED.includes(call.status) || lost) return undefined;
    let alive = true;
    const started = Date.now();
    const id = setInterval(async () => {
      if (Date.now() - started > GIVE_UP_MS) { clearInterval(id); if (alive) setLost(true); return; }
      let fresh = null;
      try { fresh = await freight.getCall(call.id); }
      catch (e) { if (e?.status === 404) { clearInterval(id); if (alive) setLost(true); } return; }
      if (!alive || !fresh) return;
      setCall(fresh);
      if (SETTLED.includes(fresh.status) && onDone) onDone(fresh);
    }, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [call && call.id, call && call.status, lost]);

  const onCall = async () => {
    if (when === "later") {
      const at = nextAt(time);
      setBusy(true); setMsg(null);
      try {
        await freight.scheduleCall(driver.id, { reason: platformReason(reason), loadId: load?.id || undefined, message: message.trim() || undefined, dueAt: at.at.toISOString() });
        setMsg({ ok: true, text: `Set. Alfred calls ${first} at ${at.at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} ${at.day}.` });
        setMessage(""); setWhen("now"); setTime("");
        if (onDone) onDone(null);
      } catch (e) { setMsg({ ok: false, text: refusalLine(first, e) }); }
      finally { setBusy(false); }
      return;
    }
    // ⛔ BEFORE ANY AWAIT: this click is what lets the page make sound, and an AI persona's call is
    // heard here, live (LiveListen.jsx). After the await it is too late.
    if (driver?.persona) primeAudio();
    setBusy(true); setMsg(null); setCall(null); setLost(false); setDoubt(false);
    try {
      const r = await freight.callDriver(driver.id, { reason: platformReason(reason), loadId: load?.id || undefined, message: message.trim() || undefined });
      setCall(r?.call || null);
      setMessage("");
      if (onDone) onDone(r?.call || null);
    } catch (e) {
      setMsg({ ok: false, text: refusalLine(first, e) });
      // A call that may have gone is still watched, and blocks a second one, like any other.
      if (e?.body?.error === "platform_timeout" && e.body.call) { setCall(e.body.call); setDoubt(true); }
      if (onDone) onDone(null);
    } finally { setBusy(false); }
  };

  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{`Call ${first}`}</h2>
      <CallForm driver={driver} reason={reason} setReason={setReason} message={message} setMessage={setMessage} busy={busy} onCall={onCall}
        when={when} setWhen={setWhen} time={time} setTime={setTime} ringing={ringing} />
      <CallProgress call={call} name={driver?.name} lost={lost} doubt={doubt} />
      {call && call.persona && ringing && (
        <LiveListen T={INK} mono={mono} callId={call.id} name={driver.name} listen={freight.listenCall} />
      )}
      <Note msg={msg} />
    </Card>
  );
}
